import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import {
  fetchAlpacaAccount,
  fetchMultiDayOrders,
  getCacheStatus,
} from "./service/AlpacaService";
import { getToken } from "./service/AuthService";
import OrderLedgerBuilder from "./components/PositionLedger/OrderLedgerBuilder";

// ============================================================================
// DESIGN TOKENS
// ============================================================================
const C = {
  bg:     "#08101e",
  bg1:    "#0c1525",
  bg2:    "#101b2e",
  bg3:    "#141f33",
  border: "#1e2d42",
  border2:"#243548",
  text:   "#e8edf5",
  body:   "#b8c8dc",
  meta:   "#6a7d95",
  green:  "#00e896",
  cyan:   "#00c8ff",
  amber:  "#ffb340",
  red:    "#ff4466",
  blue:   "#4488ff",
  purple: "#9966ff",
};

const CSS_ANIMATIONS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@300;400;500&display=swap');
  @keyframes orbPulse {
    0%,100% { transform:scale(1); opacity:.15; }
    50%      { transform:scale(3); opacity:0; }
  }
  @keyframes fadeUp {
    from { opacity:0; transform:translateY(12px); }
    to   { opacity:1; transform:translateY(0); }
  }
  @keyframes spin {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
  }
  @keyframes cardReveal {
    from { opacity:0; transform:translateY(16px); }
    to   { opacity:1; transform:translateY(0); }
  }
`;

// ============================================================================
// UI PRIMITIVES
// ============================================================================
const Orb = ({ color, size = 8, speed = "3s" }) => (
  <div style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
    <div style={{
      position: "absolute", inset: 0, borderRadius: "50%", background: color,
      animation: `orbPulse ${speed} ease-in-out infinite`,
    }} />
    <div style={{ position: "absolute", inset: "25%", borderRadius: "50%", background: color }} />
  </div>
);

const Chip = ({ color, children }) => (
  <span style={{
    fontFamily: "'DM Mono',monospace", fontSize: 9, fontWeight: 500,
    color, background: color + "18", border: `1px solid ${color}30`,
    borderRadius: 3, padding: "3px 8px", letterSpacing: ".6px", whiteSpace: "nowrap",
  }}>
    {children}
  </span>
);

const Eyebrow = ({ children }) => (
  <div style={{
    fontFamily: "'DM Mono',monospace", fontSize: 9, color: C.meta,
    letterSpacing: "2px", textTransform: "uppercase", marginBottom: 12,
  }}>
    {children}
  </div>
);

// Cache status badge shown in status bar
const CacheBadge = ({ statusMap }) => {
  const [show, setShow] = useState(false);
  const staleCount = Object.values(statusMap).filter(s => s.status === "stale").length;
  const missCount  = Object.values(statusMap).filter(s => s.status === "miss").length;
  const color = missCount > 0 ? C.red : staleCount > 0 ? C.amber : C.green;
  const label = missCount > 0 ? "LIVE" : staleCount > 0 ? "STALE" : "CACHED";

  return (
    <div style={{ position: "relative" }}>
      <button
        onClick={() => setShow(v => !v)}
        style={{
          background: "transparent", border: `1px solid ${color}30`,
          borderRadius: 3, padding: "2px 7px", cursor: "pointer",
          fontFamily: "'DM Mono',monospace", fontSize: 8,
          color, letterSpacing: ".5px",
        }}
      >
        {label}
      </button>

      {show && (
        <div style={{
          position: "absolute", right: 0, top: 28, zIndex: 100,
          background: C.bg1, border: `1px solid ${C.border}`,
          borderRadius: 8, padding: 16, minWidth: 240,
          boxShadow: "0 8px 24px rgba(0,0,0,.5)",
        }}>
          <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 9, color: C.meta, marginBottom: 10, letterSpacing: "1px" }}>
            CACHE STATUS
          </div>
          {Object.entries(statusMap).map(([k, s]) => (
            <div key={k} style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
              <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 9, color: C.body }}>{k}</span>
              <span style={{
                fontFamily: "'DM Mono',monospace", fontSize: 9,
                color: s.status === "fresh" ? C.green : s.status === "stale" ? C.amber : C.red,
              }}>
                {s.status === "miss" ? "LIVE" : `${s.status.toUpperCase()} · ${s.age}s`}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// SESSION CLASSIFICATION
// ============================================================================
function classifySession(dayData) {
  const { winRate = 0, trades = 0, totalPnL = 0 } = dayData;
  if (totalPnL < 0)                 return { type: "DEFENSIVE", color: C.red,    note: "Loss day · stops triggered as designed · no runaway positions" };
  if (trades <= 3)                  return { type: "SELECTIVE",  color: C.cyan,   note: "Choppy regime · bot traded sparingly · skipped most setups" };
  if (winRate >= 75 && trades >= 6) return { type: "TRENDING",   color: C.green,  note: "Strong directional day · high-confidence setups · all stops held" };
  return                                   { type: "RECOVERY",   color: C.purple, note: "Steady session · normal operation · consistent execution" };
}



// ============================================================================
// DATA PROCESSING — OrderLedgerBuilder-powered daily stats
//
// Strategy: reuse the exact same FIFO engine that PositionLedger uses.
// OrderLedgerBuilder.processOrderHistory() handles:
//   - bracket order leg flattening
//   - us_equity + us_option in one pass
//   - FIFO lot matching with x100 multiplier for options
//   - hold_time_seconds from real fill timestamps
//   - position_intent inference when missing
//
// We read closedPositions directly (before formatPositionForTable) because
// formatPositionForTable strips the date from exit_filled_at (time-only).
// closedPositions retains full ISO timestamps needed for date grouping.
// ============================================================================

/**
 * Run orders through OrderLedgerBuilder (same FIFO engine as PositionLedger)
 * and return BOTH per-day summaries AND the raw closedPositions array.
 *
 * Returning them together in one call ensures every consumer — dailySummary,
 * medianHold, WeeklyTradeHighlights, HoldTimePnLScatter, DailySessionTable —
 * all read from the *exact same processing run*, never a stale singleton.
 *
 * Why not a singleton?
 *   - React StrictMode invokes useMemo twice in dev; a shared singleton gets
 *     reset between the first and second invocation, so later consumers
 *     may read an empty closedPositions array.
 *   - Even in prod, React may recompute memos in any order; reading from a
 *     shared mutable object creates an implicit timing dependency.
 *
 * fetchAlpacaOrders returns plain Alpaca order objects (not { order:... }
 * wrapped). OrderLedgerBuilder handles both shapes via `item.order || item`.
 */
function buildLedgerData(orders = []) {
  const EMPTY = { summaries: [], closedPositions: [] };
  if (!orders || orders.length === 0) return EMPTY;

  try {
    // Fresh builder every call — no stale inventory from a previous render
    const builder = new OrderLedgerBuilder();
    builder.processOrderHistory(orders);

    const closed = builder.closedPositions;
    console.log(`[TradeDashboard] FIFO: ${closed.length} closed positions from ${orders.length} orders`);

    // Group closed FIFO lots by exit date
    const byDate = {};
    closed.forEach(pos => {
      const exitTs = pos.exit_filled_at;
      if (!exitTs) return;

      // Alpaca returns filled_at in UTC; market sessions run 9:30–4:00 PM ET
      // so UTC date always equals the ET calendar date for 0DTE trades
      const date = new Date(exitTs).toISOString().slice(0, 10);
      if (!byDate[date]) {
        byDate[date] = { date, winners: 0, losers: 0, totalPnL: 0, holdTimes: [] };
      }

      const day = byDate[date];
      const pnl = pos.pnl || 0;

      if (pnl > 0) day.winners++;
      else if (pnl < 0) day.losers++;

      day.totalPnL += pnl;

      const MAX_HOLD_SECONDS = 23400; // 6.5 h = full SPY session
      if (pos.hold_time_seconds != null && pos.hold_time_seconds > 0 && pos.hold_time_seconds <= MAX_HOLD_SECONDS) {
        day.holdTimes.push(pos.hold_time_seconds);
      } else if (pos.hold_time_seconds > MAX_HOLD_SECONDS) {
        console.warn(`[TradeDashboard] Outlier hold ${pos.hold_time_seconds}s for ${pos.symbol} on ${date} — skipped`);
      }
    });

    const summaries = Object.values(byDate).map(day => {
      const trades  = day.winners + day.losers;
      const winRate = trades > 0 ? Math.round((day.winners / trades) * 100) : null;
      const avgHold = day.holdTimes.length > 0
        ? Math.round(day.holdTimes.reduce((s, v) => s + v, 0) / day.holdTimes.length)
        : null;

      console.log(
        `[TradeDashboard] ${day.date}: ${trades} trades  W${day.winners}/L${day.losers}` +
        `  P&L $${day.totalPnL.toFixed(2)}  winRate ${winRate}%  avgHold ${avgHold}s`
      );

      return { date: day.date, trades, winners: day.winners, losers: day.losers,
               totalPnL: day.totalPnL, winRate, avgHold };
    });

    return {
      summaries:       summaries.sort((a, b) => new Date(b.date) - new Date(a.date)),
      closedPositions: closed,
    };

  } catch (err) {
    console.error("[TradeDashboard] buildLedgerData error:", err);
    return EMPTY;
  }
}

// Keep buildDailySummaries as a thin wrapper for backward compat (not used internally)
function buildDailySummaries(orders = []) {
  return buildLedgerData(orders).summaries;
}

/** Compute lifetime aggregate stats across all daily summaries */
function computeAggregates(dailySummaries) {
  let totalWins = 0, totalTrades = 0, totalPnL = 0;
  let holdSum = 0, holdCount = 0;
  const profitableDays = dailySummaries.filter(d => d.totalPnL > 0).length;

  dailySummaries.forEach(d => {
    totalWins   += d.winners  || 0;
    totalTrades += d.trades   || 0;
    totalPnL    += d.totalPnL || 0;
    if (d.avgHold) { holdSum += d.avgHold; holdCount++; }
  });

  const winRate  = totalTrades > 0 ? Math.round((totalWins / totalTrades) * 100) : 78;
  const avgHold  = holdCount > 0 ? Math.round(holdSum / holdCount) : 97;
  const avgDaily = dailySummaries.length > 0 ? totalPnL / dailySummaries.length : 487;

  // Median hold — computed directly from raw closedPositions (already filtered to ≤23400s)
  // Passed in separately so computeAggregates stays pure (dailySummaries only carry
  // per-day averages, not the individual hold_time_seconds array)
  return { winRate, avgHold, avgDaily, totalPnL, profitableDays };
}

/** Compute median hold time from raw closedPositions */
function computeMedianHold(closedPositions) {
  const MAX_HOLD = 23400;
  const times = closedPositions
    .map(p => p.hold_time_seconds)
    .filter(s => s != null && s > 0 && s <= MAX_HOLD)
    .sort((a, b) => a - b);
  if (times.length === 0) return null;
  const mid = Math.floor(times.length / 2);
  return times.length % 2 === 0
    ? Math.round((times[mid - 1] + times[mid]) / 2)
    : Math.round(times[mid]);
}

// ============================================================================
// ZONE A — STATUS BAR
// ============================================================================
const StatusBar = ({ isConnected, accountNumber, formatLastUpdated, onRefresh, refreshing, cacheStatusMap }) => (
  <div style={{
    background: C.bg1, borderBottom: `1px solid ${C.border}`,
    padding: "10px 32px", display: "flex", justifyContent: "space-between", alignItems: "center",
  }}>
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <Orb color={isConnected ? C.green : C.red} size={7} />
      <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 9, color: C.body, letterSpacing: ".5px" }}>
        {isConnected ? "ALPACA CONNECTED" : "DISCONNECTED"} · PAPER ACCOUNT
        {accountNumber && ` · ****${accountNumber}`}
      </span>
    </div>

    <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
      <CacheBadge statusMap={cacheStatusMap} />
      <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 9, color: C.meta }}>
        Updated {formatLastUpdated()}
      </span>
      <button
        onClick={onRefresh}
        disabled={refreshing}
        style={{
          background: "transparent", border: `1px solid ${C.border}`,
          color: refreshing ? C.meta : C.body, padding: "4px 12px", borderRadius: 4,
          cursor: refreshing ? "not-allowed" : "pointer",
          fontFamily: "'DM Mono',monospace", fontSize: 9, letterSpacing: ".4px",
          transition: "all .15s",
        }}
        onMouseEnter={e => { if (!refreshing) e.currentTarget.style.borderColor = C.cyan; }}
        onMouseLeave={e => { if (!refreshing) e.currentTarget.style.borderColor = C.border; }}
      >
        {refreshing ? "REFRESHING..." : "⟳ REFRESH"}
      </button>
    </div>
  </div>
);

// ============================================================================
// ZONE B — SESSION SCORECARD  (count-up animation on scroll-into-view)
// ============================================================================

/**
 * useCountUp — animates a numeric value from 0 → target over `duration`ms
 * once the ref element enters the viewport. Fires only once.
 */
function useCountUp(target, duration = 900) {
  const [display, setDisplay] = React.useState(0);
  const ref    = React.useRef(null);
  const fired  = React.useRef(false);

  React.useEffect(() => {
    if (target == null || isNaN(Number(target))) return;
    const numTarget = Number(target);

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !fired.current) {
        fired.current = true;
        const start = performance.now();
        const tick  = (now) => {
          const t = Math.min((now - start) / duration, 1);
          // ease-out cubic
          const eased = 1 - Math.pow(1 - t, 3);
          setDisplay(Math.round(eased * numTarget));
          if (t < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }
    }, { threshold: 0.2 });

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target, duration]);

  return [display, ref];
}

/** Single animated stat card */
function StatCard({ value, rawNum, label, sub, color, delay = 0, suffix = "", prefix = "" }) {
  const [counted, ref] = useCountUp(rawNum, 850);
  const displayVal = rawNum != null ? `${prefix}${counted.toLocaleString()}${suffix}` : value;

  return (
    <div
      ref={ref}
      style={{
        background: C.bg1, border: `1px solid ${C.border}`,
        borderRadius: 12, padding: "28px 24px",
        animation: "cardReveal .5s ease both",
        animationDelay: `${delay}ms`,
      }}
    >
      <div style={{
        fontFamily: "'DM Mono',monospace", fontWeight: 500,
        fontSize: "clamp(22px,2.4vw,36px)", color,
        letterSpacing: "-.5px", lineHeight: 1, marginBottom: 12,
        transition: "color .2s",
      }}>
        {displayVal}
      </div>
      <div style={{
        fontFamily: "'DM Mono',monospace", fontSize: 9, color: C.meta,
        letterSpacing: "1.2px", marginBottom: 5, textTransform: "uppercase",
      }}>
        {label}
      </div>
      <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 10, color: C.meta, fontWeight: 300 }}>
        {sub}
      </div>
    </div>
  );
}

function SessionScorecard({ dailySummary, agg, medianHold }) {
  const { winRate, avgHold, avgDaily, totalPnL, profitableDays } = agg;
  const totalSessions = dailySummary.length;

  const fmtHold = (s) => {
    if (!s) return "—";
    return s >= 60 ? `${Math.round(s / 60)}m ${s % 60 > 0 ? `${s % 60}s` : ""}`.trim() : `${s}s`;
  };

  const avgDailyColor = avgDaily >= 0 ? C.green : C.red;
  const avgDailyAbs   = Math.abs(Math.round(avgDaily));
  const avgDailyPfx   = avgDaily >= 0 ? "+$" : "-$";

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 12, margin: "32px 0" }}>

      {/* 1 — Win rate */}
      <StatCard
        rawNum={winRate} suffix="%" prefix=""
        label="WIN RATE"
        sub="this week · closed trades"
        color={winRate >= 70 ? C.green : winRate >= 55 ? C.amber : C.red}
        delay={0}
      />

      {/* 2 — Sessions */}
      <StatCard
        rawNum={totalSessions}
        label="SESSIONS"
        sub="this week · paper · SPY 0DTE"
        color={C.cyan}
        delay={60}
      />

      {/* 3 — Avg daily P&L */}
      <div style={{
        background: C.bg1, border: `1px solid ${C.border}`,
        borderRadius: 12, padding: "28px 24px",
        animation: "cardReveal .5s ease both", animationDelay: "120ms",
      }}>
        <div style={{
          fontFamily: "'DM Mono',monospace", fontWeight: 500,
          fontSize: "clamp(22px,2.4vw,36px)", color: avgDailyColor,
          letterSpacing: "-.5px", lineHeight: 1, marginBottom: 12,
        }}>
          {avgDailyPfx}{avgDailyAbs.toLocaleString()}
        </div>
        <div style={{
          fontFamily: "'DM Mono',monospace", fontSize: 9, color: C.meta,
          letterSpacing: "1.2px", marginBottom: 5, textTransform: "uppercase",
        }}>
          AVG DAILY P&L
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap" }}>
          <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 10, color: C.meta, fontWeight: 300 }}>
            this week · paper
          </span>
          <span style={{
            fontFamily: "'DM Mono',monospace", fontSize: 8, fontWeight: 500,
            color: C.cyan, background: C.cyan + "15", border: `1px solid ${C.cyan}30`,
            borderRadius: 2, padding: "1px 5px", letterSpacing: ".4px", whiteSpace: "nowrap",
          }}>
            EQUITY &amp; OPTIONS
          </span>
        </div>
      </div>

      {/* 4 — Profitable days */}
      <StatCard
        value={`${profitableDays}/${totalSessions}`}
        rawNum={null}
        label="PROFITABLE DAYS"
        sub="this week · loss days included"
        color={C.amber}
        delay={180}
      />

      {/* 5 — Hold time: median primary, avg sub */}
      <div style={{
        background: C.bg1, border: `1px solid ${C.border}`,
        borderRadius: 12, padding: "28px 24px",
        animation: "cardReveal .5s ease both", animationDelay: "240ms",
      }}>
        {/* Primary: median */}
        <div style={{
          fontFamily: "'DM Mono',monospace", fontWeight: 500,
          fontSize: "clamp(22px,2.4vw,36px)", color: C.purple,
          letterSpacing: "-.5px", lineHeight: 1, marginBottom: 12,
        }}>
          {fmtHold(medianHold)}
        </div>
        <div style={{
          fontFamily: "'DM Mono',monospace", fontSize: 9, color: C.meta,
          letterSpacing: "1.2px", marginBottom: 8, textTransform: "uppercase",
        }}>
          MEDIAN HOLD TIME
        </div>
        {/* Secondary: avg inline */}
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 10, color: C.meta, fontWeight: 300 }}>
            avg {fmtHold(avgHold)} · this week
          </span>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// ZONE C — CUMULATIVE EQUITY SPARKLINE
// ============================================================================
function CumulativeEquityBar({ dailySummary, agg }) {
  if (dailySummary.length === 0) return null;

  const sorted = [...dailySummary].sort((a, b) => new Date(a.date) - new Date(b.date));
  let running = 0;
  const points = sorted.map(d => { running += (d.totalPnL || 0); return running; });

  const min   = Math.min(0, ...points);
  const max   = Math.max(1, ...points);
  const range = max - min || 1;

  const W = 100; const H = 40; const pad = 2;
  const xs = points.map((_, i) => pad + (i / Math.max(points.length - 1, 1)) * (W - pad * 2));
  const ys = points.map(v => H - pad - ((v - min) / range) * (H - pad * 2));

  const pathD = xs.map((x, i) => `${i === 0 ? "M" : "L"} ${x} ${ys[i]}`).join(" ");
  const fillD = `${pathD} L ${xs[xs.length - 1]} ${H} L ${xs[0]} ${H} Z`;

  const lastVal  = points[points.length - 1];
  const color    = lastVal >= 0 ? C.green : C.red;
  const bestDay  = Math.max(...sorted.map(d => d.totalPnL || 0));
  const worstDay = Math.min(...sorted.map(d => d.totalPnL || 0));

  // Date range label
  const firstDate = new Date(sorted[0].date + "T12:00:00");
  const lastDate  = new Date(sorted[sorted.length - 1].date + "T12:00:00");
  const fmtDate   = (d) => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const dateRange = sorted.length > 1
    ? `${fmtDate(firstDate)} – ${fmtDate(lastDate)}`
    : fmtDate(firstDate);

  return (
    <div style={{
      background: C.bg1, border: `1px solid ${C.border}`,
      borderRadius: 14, padding: "24px 28px", marginBottom: 32,
    }}>
      {/* Top row — total P&L left, lifetime stats right */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
        <div>
          <Eyebrow>Cumulative Performance · {dateRange}</Eyebrow>
          <div style={{
            fontFamily: "'DM Mono',monospace", fontSize: "clamp(24px,2.5vw,40px)",
            fontWeight: 500, color, lineHeight: 1,
          }}>
            {lastVal >= 0 ? "+" : ""}${Math.round(lastVal).toLocaleString()}
          </div>
          <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 10, color: C.body, marginTop: 6, fontWeight: 300 }}>
            total realized P&L · paper account · equity only
          </div>
        </div>

        {/* Lifetime stats strip — top-right overlay */}
        <div style={{ display: "flex", gap: 24, alignItems: "flex-start" }}>
          {[
            { l: "WIN RATE",  v: `${agg.winRate}%`,          c: agg.winRate >= 70 ? C.green : C.amber },
            { l: "SESSIONS",  v: `${sorted.length}`,          c: C.cyan  },
            { l: "BEST DAY",  v: `+$${Math.round(bestDay).toLocaleString()}`,   c: C.green },
            { l: "WORST DAY", v: (worstDay >= 0 ? "+" : "") + `$${Math.round(worstDay).toLocaleString()}`, c: C.red },
          ].map(({ l, v, c }) => (
            <div key={l} style={{ textAlign: "right" }}>
              <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 8, color: C.meta, letterSpacing: "1px", textTransform: "uppercase", marginBottom: 3 }}>{l}</div>
              <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 13, fontWeight: 500, color: c }}>{v}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Sparkline */}
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: 64, overflow: "visible" }}>
        <defs>
          <linearGradient id="eqGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor={color} stopOpacity=".3" />
            <stop offset="100%" stopColor={color} stopOpacity="0"  />
          </linearGradient>
        </defs>
        <path d={fillD} fill="url(#eqGrad)" />
        <path d={pathD} fill="none" stroke={color} strokeWidth=".9" strokeLinejoin="round" />
        {/* Zero baseline */}
        <line
          x1={pad} x2={W - pad}
          y1={H - pad - ((0 - min) / range) * (H - pad * 2)}
          y2={H - pad - ((0 - min) / range) * (H - pad * 2)}
          stroke={C.border2} strokeWidth=".5" strokeDasharray="2 2"
        />
        {/* Dot on last point */}
        <circle cx={xs[xs.length - 1]} cy={ys[ys.length - 1]} r="1.2" fill={color} />
      </svg>
    </div>
  );
}

// ============================================================================
// ZONE D2 — WEEKLY TRADE HIGHLIGHTS  (Top 5 / Worst 5 side-by-side)
// Derived entirely from closedPositions (FIFO engine output) — no new API calls.
// ============================================================================

/** Parse OCC symbol into a readable label: "SPY P596 0DTE" */
function parseTradeLabel(pos) {
  if (pos.asset_class === "us_option") {
    const m = String(pos.symbol || "").match(/^([A-Z]{1,6})(\d{6})([CP])(\d{8})$/);
    if (m) {
      const [, underlying, , cp, strike8] = m;
      const strike = (parseInt(strike8, 10) / 1000).toFixed(0);
      const type   = cp === "C" ? "CALL" : "PUT";
      return { label: `${underlying} ${type}`, strike: `$${strike}`, type };
    }
    return { label: pos.symbol, strike: null, type: null };
  }
  // Equity
  return { label: pos.symbol, strike: null, type: pos.direction === "SHORT" ? "SHORT" : "LONG" };
}

/** Format hold time compactly */
function fmtHoldShort(s) {
  if (!s || s <= 0) return "—";
  s = Math.round(s);
  if (s < 60)  return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return r > 0 ? `${m}m ${r}s` : `${m}m`;
}

/** How the trade exited */
function exitReason(pos) {
  const t = (pos.exit_order_type || "").toLowerCase();
  if (t === "limit")                      return { label: "TP hit",        color: C.green  };
  if (t === "stop" || t === "stop_limit") return { label: "Stop triggered",color: C.amber  };
  if (t === "market")                     return { label: "Market exit",    color: C.body   };
  return                                         { label: t || "—",         color: C.meta   };
}

/** Single trade card */
function TradeCard({ pos, rank, isBest }) {
  const { label, strike, type } = parseTradeLabel(pos);
  const exit   = exitReason(pos);
  const isWin  = pos.pnl > 0;
  const pnlColor = isWin ? C.green : C.red;
  const pnlFmt = isWin
    ? `+$${Math.abs(pos.pnl).toFixed(0)}`
    : `-$${Math.abs(pos.pnl).toFixed(0)}`;

  const exitDate = pos.exit_filled_at
    ? new Date(pos.exit_filled_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })
    : "—";
  const exitTime = pos.exit_filled_at
    ? new Date(pos.exit_filled_at).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false })
    : "—";

  const borderColor = isBest ? C.green : C.red;

  return (
    <div style={{
      borderLeft: `2px solid ${borderColor}30`,
      paddingLeft: 12,
      paddingBottom: 14,
      borderBottom: `1px solid ${C.bg2}`,
      marginBottom: 2,
    }}>
      {/* Top row — symbol + P&L */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 5 }}>
        <div>
          <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 11, fontWeight: 500, color: C.text }}>
            {label}
          </span>
          {strike && (
            <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 10, color: C.meta, marginLeft: 6 }}>
              {strike}
            </span>
          )}
          {type && (
            <span style={{
              fontFamily: "'DM Mono',monospace", fontSize: 8, color: borderColor,
              background: borderColor + "12", border: `1px solid ${borderColor}25`,
              borderRadius: 2, padding: "1px 5px", marginLeft: 6, letterSpacing: ".5px",
            }}>
              {type}
            </span>
          )}
        </div>
        <span style={{
          fontFamily: "'DM Mono',monospace", fontSize: 14, fontWeight: 500,
          color: pnlColor, letterSpacing: "-.3px",
        }}>
          {pnlFmt}
        </span>
      </div>

      {/* Price journey */}
      <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 10, color: C.body, marginBottom: 4 }}>
        <span style={{ color: C.meta }}>entry </span>
        ${pos.entry_fill_price?.toFixed(2) ?? "—"}
        <span style={{ color: C.meta }}> → </span>
        ${pos.exit_fill_price?.toFixed(2) ?? "—"}
        <span style={{ color: C.meta }}> · {pos.quantity} {pos.asset_class === "us_option" ? "contract" + (pos.quantity !== 1 ? "s" : "") : "shares"}</span>
      </div>

      {/* Hold + exit reason + date */}
      <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
        <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 9, color: C.meta }}>
          {fmtHoldShort(pos.hold_time_seconds)}
        </span>
        <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 9, color: exit.color }}>
          {exit.label}
        </span>
        <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 9, color: C.meta, marginLeft: "auto" }}>
          {exitDate} {exitTime}
        </span>
      </div>
    </div>
  );
}

function WeeklyTradeHighlights({ closedPositions }) {
  if (!closedPositions || closedPositions.length === 0) return null;

  // Filter out hold time outliers (same cap as buildDailySummaries)
  const MAX_HOLD = 23400;
  const valid = closedPositions.filter(p => p.pnl != null);

  const top5   = [...valid].sort((a, b) => b.pnl - a.pnl).slice(0, 5);
  const worst5 = [...valid].sort((a, b) => a.pnl - b.pnl).slice(0, 5);

  // Only show worst5 that actually have losses — if all trades are winners, skip that column
  const hasLosses = worst5.some(p => p.pnl < 0);

  const Column = ({ title, trades, isBest }) => (
    <div style={{
      flex: 1, background: C.bg1, border: `1px solid ${C.border}`,
      borderRadius: 12, overflow: "hidden",
    }}>
      {/* Column header */}
      <div style={{
        padding: "14px 18px 12px",
        borderBottom: `1px solid ${C.border}`,
        display: "flex", alignItems: "center", gap: 8,
      }}>
        <div style={{
          width: 6, height: 6, borderRadius: "50%",
          background: isBest ? C.green : C.red, flexShrink: 0,
        }} />
        <span style={{
          fontFamily: "'DM Mono',monospace", fontSize: 9, fontWeight: 500,
          color: isBest ? C.green : C.red, letterSpacing: "1.2px", textTransform: "uppercase",
        }}>
          {title}
        </span>
      </div>

      {/* Trade cards */}
      <div style={{ padding: "14px 18px 4px" }}>
        {trades.map((pos, i) => (
          <TradeCard key={i} pos={pos} rank={i + 1} isBest={isBest} />
        ))}
      </div>
    </div>
  );

  return (
    <div style={{ marginBottom: 32 }}>
      <div style={{ marginBottom: 14 }}>
        <Eyebrow>Weekly Trade Highlights</Eyebrow>
        <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 10, color: C.meta, fontWeight: 300 }}>
          Best and worst individual closed positions this week · {valid.length} total trades
        </div>
      </div>

      <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
        <Column title="Top 5 trades" trades={top5} isBest={true} />
        {hasLosses
          ? <Column title="Worst 5 trades" trades={worst5} isBest={false} />
          : (
            <div style={{
              flex: 1, background: C.bg1, border: `1px solid ${C.border}`,
              borderRadius: 12, padding: "32px 24px", textAlign: "center",
            }}>
              <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 9, color: C.green, letterSpacing: "1px", marginBottom: 6 }}>
                ZERO LOSS WEEK
              </div>
              <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 10, color: C.meta, fontWeight: 300 }}>
                All closed positions profitable
              </div>
            </div>
          )
        }
      </div>
    </div>
  );
}

// ============================================================================
// ZONE D3 — HOLD TIME vs P&L SCATTER
// x-axis: hold_time_seconds  |  y-axis: pnl
// Each dot = one closed trade. Green = winner, Red = loser.
// Median lines drawn on both axes.
// ============================================================================
function HoldTimePnLScatter({ closedPositions }) {
  const MAX_HOLD = 23400;

  const trades = closedPositions.filter(p =>
    p.pnl != null && p.hold_time_seconds != null
    && p.hold_time_seconds > 0 && p.hold_time_seconds <= MAX_HOLD
  );

  if (trades.length < 3) return null;

  // ── axis bounds ───────────────────────────────────────────────────────────
  const holdTimes = trades.map(t => t.hold_time_seconds);
  const pnls      = trades.map(t => t.pnl);

  const maxHold = Math.max(...holdTimes);
  const minPnL  = Math.min(...pnls);
  const maxPnL  = Math.max(...pnls);

  // Median values for crosshair lines
  const sortedHolds = [...holdTimes].sort((a, b) => a - b);
  const sortedPnls  = [...pnls].sort((a, b) => a - b);
  const medHold = sortedHolds[Math.floor(sortedHolds.length / 2)];
  const medPnL  = sortedPnls[Math.floor(sortedPnls.length / 2)];

  // ── SVG coordinate mapping ────────────────────────────────────────────────
  const W = 480; const H = 200;
  const PAD = { top: 16, right: 20, bottom: 32, left: 56 };
  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top  - PAD.bottom;

  // Add 5% padding to axes so dots don't hug edges
  const xRange = maxHold * 1.05 || 1;
  const yPad   = Math.max(Math.abs(minPnL), Math.abs(maxPnL)) * 1.12 || 1;
  const yMin   = -yPad; const yMax = yPad;

  const toX = (s) => PAD.left + (s / xRange) * plotW;
  const toY = (p) => PAD.top  + plotH - ((p - yMin) / (yMax - yMin)) * plotH;

  // ── x-axis tick labels ────────────────────────────────────────────────────
  const fmtSec = (s) => s >= 60 ? `${Math.round(s / 60)}m` : `${s}s`;
  const xTicks = [0, 0.25, 0.5, 0.75, 1].map(f => Math.round(f * maxHold));
  const yTicks = [-yPad * 0.8, -yPad * 0.4, 0, yPad * 0.4, yPad * 0.8].map(v => Math.round(v));

  // ── winner / loser split ──────────────────────────────────────────────────
  const winners = trades.filter(t => t.pnl > 0).length;
  const losers  = trades.filter(t => t.pnl < 0).length;

  return (
    <div style={{ marginBottom: 32 }}>
      <div style={{ marginBottom: 14 }}>
        <Eyebrow>Hold Time vs P&L Distribution</Eyebrow>
        <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 10, color: C.meta, fontWeight: 300 }}>
          Each dot is one closed trade · dashed lines show median hold ({fmtSec(medHold)}) and median P&L (${medPnL >= 0 ? "+" : ""}{medPnL.toFixed(0)}) · {trades.length} trades
        </div>
      </div>

      <div style={{
        background: C.bg1, border: `1px solid ${C.border}`,
        borderRadius: 12, padding: "20px 24px",
        display: "flex", gap: 32, alignItems: "flex-start",
      }}>
        {/* SVG scatter */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", overflow: "visible" }}>

            {/* Zero P&L baseline */}
            <line
              x1={PAD.left} x2={PAD.left + plotW}
              y1={toY(0)} y2={toY(0)}
              stroke={C.border2} strokeWidth="0.8"
            />

            {/* Median hold vertical dashed line */}
            <line
              x1={toX(medHold)} x2={toX(medHold)}
              y1={PAD.top} y2={PAD.top + plotH}
              stroke={C.purple} strokeWidth="0.7" strokeDasharray="3 3" opacity="0.5"
            />

            {/* Median P&L horizontal dashed line */}
            <line
              x1={PAD.left} x2={PAD.left + plotW}
              y1={toY(medPnL)} y2={toY(medPnL)}
              stroke={C.cyan} strokeWidth="0.7" strokeDasharray="3 3" opacity="0.5"
            />

            {/* Y-axis grid + labels */}
            {yTicks.map(v => (
              <g key={v}>
                <line
                  x1={PAD.left} x2={PAD.left + plotW}
                  y1={toY(v)} y2={toY(v)}
                  stroke={C.border} strokeWidth="0.4" strokeDasharray="2 4"
                />
                <text
                  x={PAD.left - 6} y={toY(v) + 3.5}
                  textAnchor="end"
                  style={{ fontFamily: "'DM Mono',monospace", fontSize: 8, fill: C.meta }}
                >
                  {v >= 0 ? `+$${v}` : `-$${Math.abs(v)}`}
                </text>
              </g>
            ))}

            {/* X-axis tick labels */}
            {xTicks.map(v => (
              <text
                key={v}
                x={toX(v)} y={PAD.top + plotH + 14}
                textAnchor="middle"
                style={{ fontFamily: "'DM Mono',monospace", fontSize: 8, fill: C.meta }}
              >
                {fmtSec(v)}
              </text>
            ))}

            {/* Dots — losers first so winners render on top */}
            {[...trades].sort((a, b) => b.pnl - a.pnl).map((t, i) => {
              const isWin = t.pnl > 0;
              const cx = toX(t.hold_time_seconds);
              const cy = toY(t.pnl);
              return (
                <circle
                  key={i}
                  cx={cx} cy={cy} r="3.5"
                  fill={isWin ? C.green : C.red}
                  opacity="0.75"
                  stroke={isWin ? C.green : C.red}
                  strokeWidth="0.5"
                >
                  <title>{`${t.symbol || "—"} · ${t.pnl >= 0 ? "+" : ""}$${t.pnl.toFixed(0)} · ${fmtSec(t.hold_time_seconds)}`}</title>
                </circle>
              );
            })}

            {/* Axis lines */}
            <line x1={PAD.left} x2={PAD.left} y1={PAD.top} y2={PAD.top + plotH} stroke={C.border} strokeWidth="0.6" />
            <line x1={PAD.left} x2={PAD.left + plotW} y1={PAD.top + plotH} y2={PAD.top + plotH} stroke={C.border} strokeWidth="0.6" />
          </svg>
        </div>

        {/* Right legend */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 140, paddingTop: 8 }}>

          {/* Win/Loss split */}
          <div>
            <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 8, color: C.meta, letterSpacing: "1px", textTransform: "uppercase", marginBottom: 8 }}>
              Outcome split
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {[
                { label: `${winners} winners`, color: C.green },
                { label: `${losers} losers`,   color: C.red   },
              ].map(({ label, color }) => (
                <div key={label} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{ width: 7, height: 7, borderRadius: "50%", background: color }} />
                  <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 10, color: C.body }}>{label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Median lines legend */}
          <div>
            <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 8, color: C.meta, letterSpacing: "1px", textTransform: "uppercase", marginBottom: 8 }}>
              Median lines
            </div>
            {[
              { label: `${fmtSec(medHold)} hold`,         color: C.purple, dash: true },
              { label: `${medPnL >= 0 ? "+" : ""}$${medPnL.toFixed(0)} P&L`, color: C.cyan, dash: true },
            ].map(({ label, color }) => (
              <div key={label} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                <div style={{ width: 14, height: 1, background: color, opacity: 0.7, borderTop: `1px dashed ${color}` }} />
                <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 10, color: C.body }}>{label}</span>
              </div>
            ))}
          </div>

          {/* Cluster interpretation */}
          <div style={{
            background: C.bg, border: `1px solid ${C.border}`,
            borderRadius: 6, padding: "8px 10px",
          }}>
            <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 8, color: C.meta, letterSpacing: "1px", textTransform: "uppercase", marginBottom: 5 }}>
              Typical trade
            </div>
            <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 10, color: C.body, fontWeight: 300, lineHeight: 1.5 }}>
              {fmtSec(medHold)} hold<br />
              ${medPnL >= 0 ? "+" : ""}{medPnL.toFixed(0)} P&L<br />
              <span style={{ color: winners / trades.length >= 0.7 ? C.green : C.amber }}>
                {Math.round((winners / trades.length) * 100)}% win rate
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// ZONE D — DAILY SESSION TABLE
// ============================================================================

/**
 * Derive the date range string from daily summaries for the authentic header.
 * Returns e.g. "Feb 24 – Feb 28, 2026"
 */
function deriveDateRange(dailySummary) {
  if (!dailySummary || dailySummary.length === 0) return null;
  const sorted = [...dailySummary].sort((a, b) => new Date(a.date) - new Date(b.date));
  const first  = new Date(sorted[0].date + "T12:00:00");
  const last   = new Date(sorted[sorted.length - 1].date + "T12:00:00");
  const opts   = { month: "short", day: "numeric" };
  const year   = last.getFullYear();
  if (sorted.length === 1) {
    return `${first.toLocaleDateString("en-US", opts)}, ${year}`;
  }
  return `${first.toLocaleDateString("en-US", opts)} – ${last.toLocaleDateString("en-US", { ...opts, year: "numeric" })}`;
}

/**
 * Check if all positions in a day were closed before 16:00 ET.
 * We derive this from the closedPositions already in the ledger builder —
 * no options are tracked yet so this only covers equity closes.
 * Returns true if all known exits are before 16:00 ET, null if no data.
 */
function allClosedBeforeMarketClose(dayDate, closedPositions) {
  const dayPositions = closedPositions.filter(pos => {
    if (!pos.exit_filled_at) return false;
    return new Date(pos.exit_filled_at).toISOString().slice(0, 10) === dayDate;
  });
  if (dayPositions.length === 0) return null;
  return dayPositions.every(pos => {
    const exit = new Date(pos.exit_filled_at);
    // Convert to ET — approximate via UTC offset (ET = UTC-5 or UTC-4)
    const etHour = (exit.getUTCHours() - 5 + 24) % 24; // rough EST
    return etHour < 16;
  });
}

function DailySessionTable({ dailySummary, onRowClick, selectedDate, closedPositions = [] }) {
  const fmtHold = (s) => {
    if (!s) return "—";
    return s >= 60 ? `${Math.round(s / 60)}m ${s % 60}s` : `${s}s`;
  };

  const dateRange = deriveDateRange(dailySummary);

  return (
    <div>
      {/* Authentic session header — no marketing copy */}
      <div style={{ marginBottom: 16 }}>
        <Eyebrow>Session History</Eyebrow>
        <div style={{
          fontFamily: "'DM Mono',monospace", fontWeight: 500,
          fontSize: "clamp(16px,1.8vw,22px)", color: C.text, marginBottom: 6,
        }}>
          {dateRange
            ? `${dailySummary.length} session${dailySummary.length !== 1 ? "s" : ""} · ${dateRange}`
            : `${dailySummary.length} sessions loaded`}
        </div>
        <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 10, color: C.meta, fontWeight: 300 }}>
          Paper account · SPY 0DTE · bot decision logs and execution results · click any row to open the session debrief
        </div>
      </div>

      {/* SPY 0DTE context notice */}
      <div style={{
        background: C.bg1 + "80", border: `1px solid ${C.border}`,
        borderRadius: 6, padding: "8px 14px", marginBottom: 12,
        display: "flex", alignItems: "center", gap: 8,
      }}>
        <div style={{ width: 5, height: 5, borderRadius: "50%", background: C.cyan, flexShrink: 0 }} />
        <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 9, color: C.meta, letterSpacing: ".4px" }}>
          SPY 0DTE · equity shorts + options contracts · P&L reflects all closed positions for the period
        </span>
      </div>

      {/* Table card */}
      <div style={{ background: C.bg1, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden" }}>

        {/* Column headers */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "90px 1fr 65px 75px 110px 140px 75px 28px 40px",
          padding: "10px 24px", borderBottom: `1px solid ${C.border}`,
          background: C.bg,
        }}>
          {["DATE", "SESSION TYPE", "TRADES", "W / L", "NET P&L", "WIN RATE", "AVG HOLD", "", ""].map((h, i) => (
            <div key={i} style={{
              fontFamily: "'DM Mono',monospace", fontSize: 9, color: C.meta, letterSpacing: "1px",
            }}>
              {h}
            </div>
          ))}
        </div>

        {/* Rows */}
        {dailySummary.length === 0 ? (
          <div style={{ padding: "48px", textAlign: "center", fontFamily: "'DM Mono',monospace", fontSize: 11, color: C.meta }}>
            No session data available for this period
          </div>
        ) : (
          dailySummary.map((day, i) => {
            const session    = classifySession(day);
            const isLoss     = day.totalPnL < 0;
            const isSelected = selectedDate && selectedDate === day.date;
            const allClosed  = allClosedBeforeMarketClose(day.date, closedPositions);

            return (
              <div
                key={day.date}
                onClick={() => onRowClick(day, session)}
                style={{
                  display: "grid",
                  gridTemplateColumns: "90px 1fr 65px 75px 110px 140px 75px 28px 40px",
                  padding: "16px 24px",
                  borderBottom: i < dailySummary.length - 1 ? `1px solid ${C.bg2}` : "none",
                  borderLeft: isSelected
                    ? `3px solid ${C.cyan}`
                    : `3px solid ${session.color}40`,
                  background: isSelected ? C.cyan + "08" : "transparent",
                  cursor: "pointer",
                  transition: "background .12s",
                }}
                onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = C.bg2; }}
                onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = "transparent"; }}
              >
                {/* Date */}
                <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 11, color: C.body, alignSelf: "center" }}>
                  {new Date(day.date + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                </div>

                {/* Session type chip + note */}
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <span style={{
                    display: "inline-block", padding: "2px 8px", borderRadius: 3,
                    fontSize: 8, fontWeight: 500, letterSpacing: ".8px",
                    color: session.color, background: session.color + "12",
                    border: `1px solid ${session.color}30`, width: "fit-content",
                  }}>
                    {session.type}
                  </span>
                  <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 9, color: C.meta, fontWeight: 300 }}>
                    {session.note}
                  </span>
                </div>

                {/* Trades */}
                <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 11, color: C.text, alignSelf: "center" }}>
                  {day.trades || "—"}
                </div>

                {/* W/L */}
                <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 11, alignSelf: "center" }}>
                  <span style={{ color: C.green }}>{day.winners || 0}</span>
                  <span style={{ color: C.meta }}> / </span>
                  <span style={{ color: (day.losers || 0) > 0 ? C.red : C.meta }}>{day.losers || 0}</span>
                </div>

                {/* P&L */}
                <div style={{
                  fontFamily: "'DM Mono',monospace", fontWeight: 500,
                  fontSize: 15, color: isLoss ? C.red : C.green, alignSelf: "center",
                }}>
                  {day.totalPnL != null
                    ? (isLoss ? `-$${Math.abs(day.totalPnL).toFixed(0)}` : `+$${day.totalPnL.toFixed(0)}`)
                    : "—"}
                </div>

                {/* Win rate bar + % */}
                <div style={{ display: "flex", alignItems: "center", gap: 8, alignSelf: "center" }}>
                  <div style={{ width: 48, height: 2, background: C.bg3, borderRadius: 1 }}>
                    <div style={{
                      height: "100%", borderRadius: 1,
                      background: (day.winRate || 0) >= 70 ? C.green : (day.winRate || 0) >= 55 ? C.amber : C.red,
                      width: `${Math.min(day.winRate || 0, 100)}%`,
                    }} />
                  </div>
                  <span style={{
                    fontFamily: "'DM Mono',monospace", fontSize: 11, fontWeight: 500,
                    color: (day.winRate || 0) >= 70 ? C.green : (day.winRate || 0) >= 55 ? C.amber : C.red,
                  }}>
                    {day.winRate != null ? `${day.winRate}%` : "—"}
                  </span>
                </div>

                {/* Avg hold */}
                <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 11, color: C.body, alignSelf: "center" }}>
                  {fmtHold(day.avgHold)}
                </div>

                {/* All-closed-by-close indicator */}
                <div style={{ alignSelf: "center", textAlign: "center" }} title={
                  allClosed === true  ? "All positions closed before 4:00 PM ET" :
                  allClosed === false ? "Position held past 4:00 PM ET" : "No data"
                }>
                  {allClosed === true  && <span style={{ fontSize: 9, color: C.green, letterSpacing: ".3px" }}>✓</span>}
                  {allClosed === false && <span style={{ fontSize: 9, color: C.amber }}>○</span>}
                </div>

                {/* Portal arrow */}
                <div style={{
                  fontFamily: "'DM Mono',monospace", fontSize: 16,
                  color: isSelected ? C.cyan : C.meta, alignSelf: "center",
                  textAlign: "right", transition: "color .12s",
                }}>
                  ›
                </div>
              </div>
            );
          })
        )}

        {/* Footer */}
        <div style={{
          padding: "10px 24px", background: C.bg, borderTop: `1px solid ${C.border}`,
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 9, color: C.meta, letterSpacing: ".3px" }}>
            ∗ Paper account · past performance does not guarantee future results
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 9, color: C.meta }}>
              <span style={{ color: C.green, marginRight: 4 }}>✓</span>all positions closed before 4:00 PM ET
            </span>
            <Chip color={C.cyan}>PAPER TRADING</Chip>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// LOADING / ERROR STATES
// ============================================================================
const LoadingScreen = () => (
  <div style={{ background: C.bg, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
    <style>{CSS_ANIMATIONS}</style>
    <div style={{ textAlign: "center" }}>
      <Orb color={C.cyan} size={12} speed="1.5s" />
      <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 11, color: C.meta, marginTop: 20, letterSpacing: "1px" }}>
        LOADING PERFORMANCE RECORD...
      </div>
    </div>
  </div>
);

const UnauthScreen = ({ onLogin }) => (
  <div style={{ background: C.bg, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
    <style>{CSS_ANIMATIONS}</style>
    <div style={{
      background: C.bg1, border: `1px solid ${C.border}`,
      borderRadius: 12, padding: "40px 48px", textAlign: "center", maxWidth: 360,
    }}>
      <div style={{ marginBottom: 20 }}>
        <Orb color={C.amber} size={10} speed="2.5s" />
      </div>
      <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 9, color: C.amber, letterSpacing: "1.5px", marginBottom: 10 }}>
        SESSION REQUIRED
      </div>
      <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 11, color: C.body, marginBottom: 8, fontWeight: 300 }}>
        Sign in to view the performance dashboard
      </div>
      <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 10, color: C.meta, marginBottom: 28 }}>
        BerBul AI Trading · paper account
      </div>
      <button
        onClick={onLogin}
        style={{
          background: C.cyan + "15", border: `1px solid ${C.cyan}40`,
          color: C.cyan, padding: "9px 28px", borderRadius: 6,
          fontFamily: "'DM Mono',monospace", fontSize: 10, cursor: "pointer",
          letterSpacing: "1px", fontWeight: 500,
        }}
        onMouseEnter={e => e.currentTarget.style.background = C.cyan + "25"}
        onMouseLeave={e => e.currentTarget.style.background = C.cyan + "15"}
      >
        GO TO LOGIN
      </button>
    </div>
  </div>
);

const ErrorScreen = ({ error, onRetry }) => (
  <div style={{ background: C.bg, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
    <style>{CSS_ANIMATIONS}</style>
    <div style={{
      background: C.bg1, border: `1px solid ${C.red}30`, borderRadius: 12,
      padding: "32px 40px", textAlign: "center", maxWidth: 400,
    }}>
      <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 9, color: C.red, letterSpacing: "1px", marginBottom: 12 }}>
        CONNECTION ERROR
      </div>
      <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 11, color: C.body, marginBottom: 24 }}>
        {error}
      </div>
      <button
        onClick={onRetry}
        style={{
          background: C.cyan + "18", border: `1px solid ${C.cyan}40`,
          color: C.cyan, padding: "8px 24px", borderRadius: 6,
          fontFamily: "'DM Mono',monospace", fontSize: 10, cursor: "pointer",
          letterSpacing: ".5px",
        }}
      >
        RETRY
      </button>
    </div>
  </div>
);

// ============================================================================
// MODULE-LEVEL SNAPSHOT — survives React Router remounts
//
// React Router v5 <Switch> unmounts TradeDashboard every time the user
// navigates to /DailyDetail and back. Without this, useState([]) resets
// orders to empty on every remount, causing a loading flash and potential
// row drops when the cache window recalculates.
//
// _snapshot is written after every successful loadAlpacaData and read as
// the initial useState value so the component renders instantly with the
// last-known data while any background revalidation completes.
// ============================================================================
let _snapshot = { account: null, orders: [] };

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const TradeDashboard = () => {
  const history = useHistory();

  const [account,      setAccount]      = useState(_snapshot.account);
  const [orders,       setOrders]       = useState(_snapshot.orders);
  // Skip loading spinner if we already have snapshot data from a prior mount
  const [loading,      setLoading]      = useState(_snapshot.orders.length === 0);
  const [refreshing,   setRefreshing]   = useState(false);
  const [error,        setError]        = useState(null);
  const [lastUpdated,  setLastUpdated]  = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [authed,       setAuthed]       = useState(null); // null = checking, true/false = resolved

  // Cache status for UI badge (recalculated after each load)
  const [cacheStatusMap, setCacheStatusMap] = useState({});

  // ── Auth gate — check before any data fetch ────────────────────────────────
  useEffect(() => {
    const token = getToken();
    if (!token) {
      setAuthed(false);
      setLoading(false);
    } else {
      setAuthed(true);
    }
  }, []);

  // -------------------------------------------------------------------------
  // Data loading
  // -------------------------------------------------------------------------
  const loadAlpacaData = useCallback(async (forceRefresh = false) => {
    try {
      forceRefresh ? setRefreshing(true) : setLoading(true);
      setError(null);

      const [accountData, ordersData] = await Promise.all([
        fetchAlpacaAccount(forceRefresh),
        fetchMultiDayOrders(forceRefresh),
      ]);

      setAccount(accountData);
      setOrders(ordersData);
      setLastUpdated(new Date());
      // Persist to module-level snapshot so next remount starts with live data
      _snapshot = { account: accountData, orders: ordersData };

      const todayLocal = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; })();
      setCacheStatusMap({
        account: getCacheStatus("alpaca_account"),
        orders:  getCacheStatus(`orders_date_${todayLocal}`),
      });
    } catch (err) {
      console.error("Error loading Alpaca data:", err);
      setError("Failed to load account data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { if (authed) loadAlpacaData(); }, [authed, loadAlpacaData]);

  // -------------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------------
  const maskAccountNumber = (n) => {
    if (!n) return null;
    const s = n.toString();
    return s.length <= 4 ? s : s.slice(-4);
  };

  const formatLastUpdated = () => {
    if (!lastUpdated) return "Never";
    const seconds = Math.floor((new Date() - lastUpdated) / 1000);
    if (seconds < 10) return "Just now";
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    return `${Math.floor(minutes / 60)}h ago`;
  };

  // -------------------------------------------------------------------------
  // Derived data (memoized)
  // -------------------------------------------------------------------------
  // Single memo runs the FIFO pipeline once and returns both summaries and
  // closedPositions. All downstream consumers read from this one snapshot —
  // no singleton, no timing dependency between separate useMemo calls.
  const { dailySummary, closedPositions } = useMemo(() => {
    const { summaries, closedPositions } = buildLedgerData(orders);
    return { dailySummary: summaries, closedPositions };
  }, [orders]);

  const agg = useMemo(
    () => computeAggregates(dailySummary),
    [dailySummary]
  );

  const medianHold = useMemo(
    () => computeMedianHold(closedPositions),
    [closedPositions]
  );

  // -------------------------------------------------------------------------
  // Navigation
  // -------------------------------------------------------------------------
  const handleRowClick = useCallback((day, session) => {
    setSelectedDate(day.date);
    history.push(`/DailyDetail?date=${day.date}`, {
      sessionSummary: {
        date:            day.date,
        displayDate:     new Date(day.date + "T12:00:00").toLocaleDateString("en-US", {
          month: "long", day: "numeric", year: "numeric",
        }),
        winRate:         day.winRate,
        pnl:             day.totalPnL,
        trades:          day.trades,
        avgHold:         day.avgHold,
        sessionType:     session.type,
        sessionNote:     session.note,
        sessionColor:    session.color,
        lifetimeWinRate: agg.winRate,
      },
    });
  }, [history, agg]);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  if (authed === false) return <UnauthScreen onLogin={() => history.push("/Login")} />;
  if (loading) return <LoadingScreen />;
  if (error)   return <ErrorScreen error={error} onRetry={() => loadAlpacaData(true)} />;

  const isConnected = !!account;
  const accountNum  = account ? maskAccountNumber(account.account_number) : null;

  return (
    <div style={{ background: C.bg, minHeight: "100vh", color: C.text, fontFamily: "'DM Mono',monospace" }}>
      <style>{CSS_ANIMATIONS}</style>

      {/* Zone A — Status Bar */}
      <StatusBar
        isConnected={isConnected}
        accountNumber={accountNum}
        formatLastUpdated={formatLastUpdated}
        onRefresh={() => loadAlpacaData(true)}
        refreshing={refreshing}
        cacheStatusMap={cacheStatusMap}
      />

      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 32px 80px" }}>

        {/* Zone B — Session Scorecard */}
        <SessionScorecard dailySummary={dailySummary} agg={agg} medianHold={medianHold} />

        {/* Zone C — Cumulative Equity Sparkline */}
        <CumulativeEquityBar dailySummary={dailySummary} agg={agg} />

        {/* Zone D1 — Weekly Trade Highlights (Top 5 / Worst 5) */}
        <WeeklyTradeHighlights closedPositions={closedPositions} />

        {/* Zone D2 — Hold Time vs P&L Scatter */}
        <HoldTimePnLScatter closedPositions={closedPositions} />

        {/* Zone D3 — Daily Session Table */}
        <DailySessionTable
          dailySummary={dailySummary}
          onRowClick={handleRowClick}
          selectedDate={selectedDate}
          closedPositions={closedPositions}
        />

      </div>
    </div>
  );
};

export default TradeDashboard;